import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { QuoteFlowLayout } from '#app/components/cotizacion/QuoteFlowLayout.tsx'
import { CarDetailsScreen } from '#app/components/cotizacion/screens/CarDetailsScreen.tsx'
import { ComparisonScreen } from '#app/components/cotizacion/screens/ComparisonScreen.tsx'
import { FinalScreen } from '#app/components/cotizacion/screens/FinalScreen.tsx'
import { PersonalInfoScreen } from '#app/components/cotizacion/screens/PersonalInfoScreen.tsx'
import { PlanSelectionScreen } from '#app/components/cotizacion/screens/PlanSelectionScreen.tsx'
import { PreAutoStepScreen } from '#app/components/cotizacion/screens/PreAutoStepScreen.tsx'
import { StepSelectionScreen } from '#app/components/cotizacion/screens/StepSelectionScreen.tsx'
import { Modal } from '#app/components/ui/modal.tsx'
import { ProgressBar } from '#app/components/ui/progress-bar.tsx'
import { type Route } from './+types/cotizacion-auto'

export const meta: Route.MetaFunction = () => [
	{ title: '100prevalor - Cotización Seguro de Auto' },
	{
		name: 'description',
		content:
			'Cotiza tu seguro de auto en simples pasos. Obtén la mejor cobertura al mejor precio.',
	},
]

export default function CotizacionAutoPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)

	// Track completed steps - step 1 is always accessible
	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)

	const [quoteData, setQuoteData] = useState({
		carBrand: '',
		carModel: '',
		carYear: '',
		carVersion: '',
		carUsage: '',
		carLocation: '',
		licensePlate: '',
		fullName: '',
		email: '',
		phone: '',
		dni: '',
		selectedPlan: '',
	})

	const totalSteps = 7

	// Calculate progress based on step
	const getProgressForStep = (currentStep: number): number => {
		const progressMap: Record<number, number> = {
			1: 0, // StepSelectionScreen - no progress bar
			2: 0, // PreAutoStepScreen - no progress bar
			3: 5, // CarDetailsScreen
			4: 37, // PersonalInfoScreen
			5: 60, // PlanSelectionScreen
			6: 95, // ComparisonScreen
			7: 100, // FinalScreen
		}
		return progressMap[currentStep] || 0
	}

	const currentProgress = getProgressForStep(step)
	const showProgressBar = step >= 3 // Only show from CarDetailsScreen onwards

	// Validate step access
	useEffect(() => {
		if (!completedSteps.has(step)) {
			// Redirect to last completed step
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted) })
		}
	}, [step, completedSteps, setSearchParams])

	const handleNext = (data?: Record<string, any>) => {
		if (data) {
			setQuoteData((prev) => ({ ...prev, ...data }))
		}

		// Check if we need to skip to a specific step (for direct selection)
		const targetStep = data?.skipToStep

		if (targetStep && targetStep <= totalSteps) {
			// Mark all steps up to target as accessible
			const stepsToComplete = Array.from(
				{ length: targetStep },
				(_, i) => i + 1,
			)
			setCompletedSteps((prev) => new Set([...prev, ...stepsToComplete]))
			setSearchParams({ step: String(targetStep) })
		} else if (step < totalSteps) {
			// Normal flow: go to next step
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			setSearchParams({ step: String(step + 1) })
		}
	}

	const handleBack = () => {
		if (step > 1) {
			setSearchParams({ step: String(step - 1) })
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return <StepSelectionScreen onNext={handleNext} />
			case 2:
				return (
					<PreAutoStepScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 3:
				return (
					<CarDetailsScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 4:
				return (
					<PersonalInfoScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 5:
				return (
					<PlanSelectionScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 6:
				return (
					<ComparisonScreen
						onNext={handleNext}
						onBack={handleBack}
						data={quoteData}
					/>
				)
			case 7:
				return <FinalScreen onBack={handleBack} data={quoteData} />
			default:
				return <StepSelectionScreen onNext={handleNext} />
		}
	}

	return (
		<>
			{/* Mobile: full page */}
			<div className="md:hidden">
				<QuoteFlowLayout
					currentStep={step}
					totalSteps={totalSteps}
					onBack={step > 1 ? handleBack : undefined}
					onClose={step === 1 ? handleClose : undefined}
					progressBar={
						showProgressBar ? (
							<ProgressBar progress={currentProgress} icon="car-04" />
						) : undefined
					}
				>
					{renderStep()}
				</QuoteFlowLayout>
			</div>

			{/* Desktop: modal */}
			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={false}
					maxWidth="xl"
				>
					<QuoteFlowLayout
						currentStep={step}
						totalSteps={totalSteps}
						onBack={step > 1 ? handleBack : undefined}
						onClose={step === 1 ? handleClose : undefined}
						progressBar={
							showProgressBar ? (
								<ProgressBar progress={currentProgress} icon="car-04" />
							) : undefined
						}
					>
						{renderStep()}
					</QuoteFlowLayout>
				</Modal>
			</div>
		</>
	)
}
